// Package Imports
import { useEffect } from 'react'

// Core Imports
import { useThemeStore } from '@/core/stores/theme.store'

// Module Imports
import { useAuth } from '@/core/hooks/use-auth'

export type { TThemeName } from '@/modules/auth/auth.types'

/**
 * Drop-in replacement for the old context-based useTheme. State lives in
 * `useThemeStore` (zustand). The store is hydrated from the authenticated
 * user's persisted theme on first auth success.
 */
export const useTheme = () => {
  const theme = useThemeStore((s) => s.theme)
  const setTheme = useThemeStore((s) => s.setTheme)
  const toggleTheme = useThemeStore((s) => s.toggleTheme)

  return { theme, setTheme, toggleTheme }
}

/**
 * Mount once at the app root. Pushes the authenticated admin's persisted
 * `theme` into the store so document + UI reflect their saved choice.
 */
export const useThemeSync = (): void => {
  const { user } = useAuth()
  const hydrate = useThemeStore((s) => s.hydrate)
  const theme = useThemeStore((s) => s.theme)

  useEffect(() => {
    if (!user) return
    if (user.theme !== theme) {
      hydrate(user.theme)
    }
  }, [user, hydrate, theme])

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.documentElement.dataset.theme = theme
  }, [theme])
}
